import {
  db,
  collection,
  doc,
  getDoc,
  getDocs,
  addDoc,
  updateDoc,
  deleteDoc,
  query,
  orderBy,
  where,
  serverTimestamp,
} from "../../../config/firebase";
import { ERROR_MESSAGES } from "../types/accountsTypes";
import {
  generateCode,
  calculateInvoiceTotals,
  calculateBillTotals,
  validateJournalLines,
  calculateJournalTotals,
} from "../utils/accountsHelpers";

class AccountsService {
  collectionRef(name) {
    return collection(db, name);
  }

  convertTimestamp(value) {
    if (!value) return null;
    if (typeof value === "object" && value.seconds !== undefined) {
      return new Date(value.seconds * 1000).toISOString();
    }
    if (value instanceof Date) return value.toISOString();
    return value;
  }

  convertDocData(data) {
    if (!data) return data;
    const converted = { ...data };
    [
      "createdAt",
      "updatedAt",
      "invoiceDate",
      "dueDate",
      "billDate",
      "transactionDate",
      "postingDate",
      "receiptDate",
      "paymentDate",
      "valueDate",
      "lastReconciledOn",
    ].forEach((key) => {
      if (converted[key])
        converted[key] = this.convertTimestamp(converted[key]);
    });
    return converted;
  }

  handleError(error) {
    console.error(error);
    return new Error(error.message || "Accounts service error");
  }

  // ACCOUNTS CHART
  async getAccounts(filters = {}) {
    try {
      let q = query(
        this.collectionRef("accounts_chart"),
        orderBy("accountCode", "asc"),
      );
      const snap = await getDocs(q);
      let rows = snap.docs.map((d) => ({
        id: d.id,
        ...this.convertDocData(d.data()),
      }));

      if (filters.mainType && filters.mainType !== "all") {
        rows = rows.filter((r) => r.mainType === filters.mainType);
      }
      if (filters.status && filters.status !== "all") {
        rows = rows.filter((r) => r.status === filters.status);
      }
      if (filters.search) {
        const s = filters.search.toLowerCase();
        rows = rows.filter(
          (r) =>
            r.accountCode?.toLowerCase().includes(s) ||
            r.accountName?.toLowerCase().includes(s) ||
            r.displayName?.toLowerCase().includes(s),
        );
      }

      return rows.filter((r) => r.isArchived !== true);
    } catch (error) {
      throw this.handleError(error);
    }
  }

  async createAccount(data) {
    try {
      if (!data.accountCode)
        throw new Error(ERROR_MESSAGES.ACCOUNT_CODE_REQUIRED);
      if (!data.accountName)
        throw new Error(ERROR_MESSAGES.ACCOUNT_NAME_REQUIRED);

      const payload = {
        accountCode: data.accountCode,
        accountName: data.accountName,
        displayName: data.displayName || data.accountName,
        mainType: data.mainType,
        subType: data.subType || "",
        normalBalance: data.normalBalance || "debit",
        parentAccountId: data.parentAccountId || null,
        hierarchy: data.hierarchy || {
          level: 1,
          path: [data.accountCode],
          rootAccountCode: data.accountCode,
        },
        controlFlags: data.controlFlags || {
          isSystem: false,
          isControlAccount: false,
          allowsManualPosting: true,
          requiresSubledger: false,
          subledgerType: null,
        },
        balances: data.balances || {
          openingDebit: 0,
          openingCredit: 0,
          currentDebit: 0,
          currentCredit: 0,
          closingBalance: 0,
          closingBalanceType: "debit",
          lastPostedAt: null,
        },
        reporting: data.reporting || {
          cashFlowGroup: "",
          balanceSheetGroup: "",
          profitLossGroup: "",
          taxRelevant: false,
        },
        tags: data.tags || [],
        notes: data.notes || "",
        status: data.status || "active",
        isArchived: false,
        createdAt: serverTimestamp(),
        updatedAt: serverTimestamp(),
        createdBy: data.createdBy || "admin",
        updatedBy: data.createdBy || "admin",
      };

      const ref = await addDoc(this.collectionRef("accounts_chart"), payload);
      const created = await getDoc(ref);
      return { id: created.id, ...this.convertDocData(created.data()) };
    } catch (error) {
      throw this.handleError(error);
    }
  }

  async updateAccount(id, data) {
    try {
      const ref = doc(db, "accounts_chart", id);
      await updateDoc(ref, { ...data, updatedAt: serverTimestamp() });
      const updated = await getDoc(ref);
      return { id: updated.id, ...this.convertDocData(updated.data()) };
    } catch (error) {
      throw this.handleError(error);
    }
  }

  async deleteAccount(id) {
    try {
      const ref = doc(db, "accounts_chart", id);
      await updateDoc(ref, {
        isArchived: true,
        status: "archived",
        updatedAt: serverTimestamp(),
      });
      return id;
    } catch (error) {
      throw this.handleError(error);
    }
  }

  // INVOICES
  async getInvoices(filters = {}) {
    try {
      const q = query(
        this.collectionRef("accounts_sales_invoices"),
        orderBy("invoiceDate", "desc"),
      );
      const snap = await getDocs(q);
      let rows = snap.docs.map((d) => ({
        id: d.id,
        ...this.convertDocData(d.data()),
      }));

      if (filters.status && filters.status !== "all") {
        rows = rows.filter((r) => r.status === filters.status);
      }
      if (filters.search) {
        const s = filters.search.toLowerCase();
        rows = rows.filter(
          (r) =>
            r.invoiceNumber?.toLowerCase().includes(s) ||
            r.partySnapshot?.name?.toLowerCase().includes(s),
        );
      }
      return rows.filter((r) => r.isArchived !== true);
    } catch (error) {
      throw this.handleError(error);
    }
  }

  async createInvoice(data) {
    try {
      const totals = calculateInvoiceTotals(data.items || []);
      const payload = {
        invoiceNumber: data.invoiceNumber || generateCode("INV"),
        invoiceSeries: data.invoiceSeries || `INV-${new Date().getFullYear()}`,
        fiscalYearId: data.fiscalYearId || "",
        customerId: data.customerId || "",
        crmClientId: data.crmClientId || "",
        crmProjectId: data.crmProjectId || "",
        quotationId: data.quotationId || "",
        invoiceDate: data.invoiceDate || new Date().toISOString().slice(0, 10),
        dueDate: data.dueDate || "",
        servicePeriod: data.servicePeriod || { from: "", to: "" },
        status: data.status || "draft",
        currency: data.currency || "INR",
        exchangeRate: data.exchangeRate || 1,
        partySnapshot: data.partySnapshot || {},
        items: data.items || [],
        totals: {
          ...totals,
          itemDiscountTotal: 0,
          documentDiscountTotal: 0,
          roundOff: 0,
          paidAmount: Number(data.paidAmount || 0),
          outstandingAmount: totals.grandTotal - Number(data.paidAmount || 0),
        },
        accounting: data.accounting || {
          arAccountId: "",
          salesAccountIds: [],
          taxAccountIds: [],
          journalId: "",
          isPostedToLedger: false,
          postedAt: null,
        },
        collection: data.collection || {
          paymentTerms: "",
          expectedCollectionDate: "",
          lastReminderAt: null,
          reminderCount: 0,
        },
        notes: data.notes || "",
        isArchived: false,
        createdAt: serverTimestamp(),
        updatedAt: serverTimestamp(),
        createdBy: data.createdBy || "admin",
        updatedBy: data.createdBy || "admin",
      };

      const ref = await addDoc(
        this.collectionRef("accounts_sales_invoices"),
        payload,
      );
      const created = await getDoc(ref);
      return { id: created.id, ...this.convertDocData(created.data()) };
    } catch (error) {
      throw this.handleError(error);
    }
  }

  async updateInvoice(id, data) {
    try {
      const ref = doc(db, "accounts_sales_invoices", id);
      await updateDoc(ref, { ...data, updatedAt: serverTimestamp() });
      const updated = await getDoc(ref);
      return { id: updated.id, ...this.convertDocData(updated.data()) };
    } catch (error) {
      throw this.handleError(error);
    }
  }

  // BILLS
  async getBills(filters = {}) {
    try {
      const q = query(
        this.collectionRef("accounts_purchase_bills"),
        orderBy("billDate", "desc"),
      );
      const snap = await getDocs(q);
      let rows = snap.docs.map((d) => ({
        id: d.id,
        ...this.convertDocData(d.data()),
      }));

      if (filters.status && filters.status !== "all") {
        rows = rows.filter((r) => r.status === filters.status);
      }
      if (filters.search) {
        const s = filters.search.toLowerCase();
        rows = rows.filter(
          (r) =>
            r.billNumber?.toLowerCase().includes(s) ||
            r.vendorSnapshot?.name?.toLowerCase().includes(s),
        );
      }
      return rows.filter((r) => r.isArchived !== true);
    } catch (error) {
      throw this.handleError(error);
    }
  }

  async createBill(data) {
    try {
      const totals = calculateBillTotals(data.items || []);
      const payload = {
        billNumber: data.billNumber || generateCode("BILL"),
        vendorBillNumber: data.vendorBillNumber || "",
        fiscalYearId: data.fiscalYearId || "",
        vendorId: data.vendorId || "",
        billDate: data.billDate || new Date().toISOString().slice(0, 10),
        dueDate: data.dueDate || "",
        status: data.status || "draft",
        currency: data.currency || "INR",
        exchangeRate: data.exchangeRate || 1,
        vendorSnapshot: data.vendorSnapshot || {},
        items: data.items || [],
        totals: {
          ...totals,
          discountTotal: 0,
          roundOff: 0,
          paidAmount: Number(data.paidAmount || 0),
          outstandingAmount: totals.grandTotal - Number(data.paidAmount || 0),
        },
        accounting: data.accounting || {
          apAccountId: "",
          expenseAccountIds: [],
          taxAccountIds: [],
          journalId: "",
          isPostedToLedger: false,
          postedAt: null,
        },
        notes: data.notes || "",
        isArchived: false,
        createdAt: serverTimestamp(),
        updatedAt: serverTimestamp(),
        createdBy: data.createdBy || "admin",
        updatedBy: data.createdBy || "admin",
      };

      const ref = await addDoc(
        this.collectionRef("accounts_purchase_bills"),
        payload,
      );
      const created = await getDoc(ref);
      return { id: created.id, ...this.convertDocData(created.data()) };
    } catch (error) {
      throw this.handleError(error);
    }
  }

  async updateBill(id, data) {
    try {
      const ref = doc(db, "accounts_purchase_bills", id);
      await updateDoc(ref, { ...data, updatedAt: serverTimestamp() });
      const updated = await getDoc(ref);
      return { id: updated.id, ...this.convertDocData(updated.data()) };
    } catch (error) {
      throw this.handleError(error);
    }
  }

  // JOURNALS
  async getJournals(filters = {}) {
    try {
      const q = query(
        this.collectionRef("accounts_journals"),
        orderBy("transactionDate", "desc"),
      );
      const snap = await getDocs(q);
      let rows = snap.docs.map((d) => ({
        id: d.id,
        ...this.convertDocData(d.data()),
      }));

      if (filters.status && filters.status !== "all") {
        rows = rows.filter((r) => r.status === filters.status);
      }
      if (filters.type && filters.type !== "all") {
        rows = rows.filter((r) => r.journalType === filters.type);
      }
      if (filters.search) {
        const s = filters.search.toLowerCase();
        rows = rows.filter(
          (r) =>
            r.journalNumber?.toLowerCase().includes(s) ||
            r.narration?.toLowerCase().includes(s),
        );
      }

      return rows;
    } catch (error) {
      throw this.handleError(error);
    }
  }

  async createJournal(data) {
    try {
      const validation = validateJournalLines(data.lines || []);
      if (!validation.valid) throw new Error(validation.message);

      const totals = calculateJournalTotals(data.lines || []);
      const journalPayload = {
        journalNumber: data.journalNumber || generateCode("JV"),
        journalType: data.journalType || "general",
        source: data.source || "manual",
        sourceId: data.sourceId || "",
        sourceNumber: data.sourceNumber || "",
        transactionDate:
          data.transactionDate || new Date().toISOString().slice(0, 10),
        postingDate: data.postingDate || new Date().toISOString().slice(0, 10),
        fiscalYearId: data.fiscalYearId || "",
        periodId: data.periodId || "",
        narration: data.narration || "",
        referenceNo: data.referenceNo || "",
        status: data.status || "draft",
        totals: {
          totalDebit: totals.totalDebit,
          totalCredit: totals.totalCredit,
          lineCount: (data.lines || []).length,
          isBalanced: totals.isBalanced,
        },
        posting: {
          postedBy: "",
          postedAt: null,
          reversedBy: "",
          reversedAt: null,
          reversalJournalId: null,
          lockVersion: 1,
        },
        links: data.links || {},
        notes: data.notes || "",
        createdAt: serverTimestamp(),
        updatedAt: serverTimestamp(),
        createdBy: data.createdBy || "admin",
        updatedBy: data.createdBy || "admin",
      };

      const journalRef = await addDoc(
        this.collectionRef("accounts_journals"),
        journalPayload,
      );

      for (let i = 0; i < (data.lines || []).length; i++) {
        const line = data.lines[i];
        await addDoc(this.collectionRef("accounts_journal_entries"), {
          journalId: journalRef.id,
          lineNo: i + 1,
          accountId: line.accountId,
          accountCode: line.accountCode || "",
          accountName: line.accountName || "",
          subledger: line.subledger || {
            type: "",
            entityId: "",
            entityName: "",
          },
          transactionDate:
            data.transactionDate || new Date().toISOString().slice(0, 10),
          postingDate:
            data.postingDate || new Date().toISOString().slice(0, 10),
          debit: Number(line.debit || 0),
          credit: Number(line.credit || 0),
          currency: line.currency || "INR",
          exchangeRate: line.exchangeRate || 1,
          baseDebit: Number(line.debit || 0),
          baseCredit: Number(line.credit || 0),
          dimensions: line.dimensions || {},
          narration: line.narration || data.narration || "",
          source: line.source || {
            sourceType: data.source || "manual",
            sourceId: data.sourceId || "",
            sourceLineId: null,
          },
          createdAt: serverTimestamp(),
          createdBy: data.createdBy || "admin",
        });
      }

      const created = await getDoc(journalRef);
      return { id: created.id, ...this.convertDocData(created.data()) };
    } catch (error) {
      throw this.handleError(error);
    }
  }

  async updateJournal(id, data) {
    try {
      const ref = doc(db, "accounts_journals", id);
      await updateDoc(ref, { ...data, updatedAt: serverTimestamp() });
      const updated = await getDoc(ref);
      return { id: updated.id, ...this.convertDocData(updated.data()) };
    } catch (error) {
      throw this.handleError(error);
    }
  }

  async getJournalLines(journalId) {
    try {
      const q = query(
        this.collectionRef("accounts_journal_entries"),
        where("journalId", "==", journalId),
        orderBy("lineNo", "asc"),
      );
      const snap = await getDocs(q);
      return snap.docs.map((d) => ({
        id: d.id,
        ...this.convertDocData(d.data()),
      }));
    } catch (error) {
      throw this.handleError(error);
    }
  }

  // RECEIPTS
  async createReceipt(data) {
    try {
      const payload = {
        receiptNumber: data.receiptNumber || generateCode("RCT"),
        customerId: data.customerId || "",
        crmClientId: data.crmClientId || "",
        receiptDate: data.receiptDate || new Date().toISOString().slice(0, 10),
        valueDate: data.valueDate || "",
        status: data.status || "received",
        amount: data.amount || {
          receivedAmount: 0,
          unallocatedAmount: 0,
          currency: "INR",
          exchangeRate: 1,
        },
        paymentMethod: data.paymentMethod || "upi",
        instrument: data.instrument || {},
        depositTo: data.depositTo || {},
        allocations: data.allocations || [],
        accounting: data.accounting || {},
        recon: data.recon || {
          status: "not_reconciled",
          bankTxnId: null,
          reconciledAt: null,
        },
        notes: data.notes || "",
        attachmentIds: data.attachmentIds || [],
        createdAt: serverTimestamp(),
        updatedAt: serverTimestamp(),
        createdBy: data.createdBy || "admin",
        updatedBy: data.createdBy || "admin",
      };

      const ref = await addDoc(
        this.collectionRef("accounts_receipts"),
        payload,
      );
      const created = await getDoc(ref);
      return { id: created.id, ...this.convertDocData(created.data()) };
    } catch (error) {
      throw this.handleError(error);
    }
  }

  // PAYMENTS
  async createPayment(data) {
    try {
      const payload = {
        paymentNumber: data.paymentNumber || generateCode("PAY"),
        vendorId: data.vendorId || "",
        paymentDate: data.paymentDate || new Date().toISOString().slice(0, 10),
        valueDate: data.valueDate || "",
        status: data.status || "paid",
        amount: data.amount || {
          paidAmount: 0,
          unallocatedAmount: 0,
          currency: "INR",
          exchangeRate: 1,
        },
        paymentMethod: data.paymentMethod || "bank_transfer",
        instrument: data.instrument || {},
        paidFrom: data.paidFrom || {},
        allocations: data.allocations || [],
        accounting: data.accounting || {},
        recon: data.recon || {
          status: "not_reconciled",
          bankTxnId: null,
          reconciledAt: null,
        },
        notes: data.notes || "",
        attachmentIds: data.attachmentIds || [],
        createdAt: serverTimestamp(),
        updatedAt: serverTimestamp(),
        createdBy: data.createdBy || "admin",
        updatedBy: data.createdBy || "admin",
      };

      const ref = await addDoc(
        this.collectionRef("accounts_payments"),
        payload,
      );
      const created = await getDoc(ref);
      return { id: created.id, ...this.convertDocData(created.data()) };
    } catch (error) {
      throw this.handleError(error);
    }
  }

  // BANK ACCOUNTS
  async getBankAccounts() {
    try {
      const q = query(
        this.collectionRef("accounts_bank_accounts"),
        orderBy("accountName", "asc"),
      );
      const snap = await getDocs(q);
      return snap.docs.map((d) => ({
        id: d.id,
        ...this.convertDocData(d.data()),
      }));
    } catch (error) {
      throw this.handleError(error);
    }
  }

  async createBankAccount(data) {
    try {
      const payload = {
        bankAccountCode: data.bankAccountCode || generateCode("BANK"),
        accountName: data.accountName || "",
        bankName: data.bankName || "",
        branchName: data.branchName || "",
        accountNumberMasked: data.accountNumberMasked || "",
        ifscCode: data.ifscCode || "",
        accountType: data.accountType || "current",
        glAccountId: data.glAccountId || "",
        openingBalance: Number(data.openingBalance || 0),
        currentBookBalance: Number(data.currentBookBalance || 0),
        currentBankBalance: Number(data.currentBankBalance || 0),
        ownerEntity: data.ownerEntity || {},
        status: data.status || "active",
        lastReconciledOn: data.lastReconciledOn || null,
        notes: data.notes || "",
        createdAt: serverTimestamp(),
        updatedAt: serverTimestamp(),
        createdBy: data.createdBy || "admin",
        updatedBy: data.createdBy || "admin",
      };

      const ref = await addDoc(
        this.collectionRef("accounts_bank_accounts"),
        payload,
      );
      const created = await getDoc(ref);
      return { id: created.id, ...this.convertDocData(created.data()) };
    } catch (error) {
      throw this.handleError(error);
    }
  }
}

const accountsService = new AccountsService();
export default accountsService;
